import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Languages, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

const sessionId = (() => {
  let s = localStorage.getItem("solaria_session");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("solaria_session", s);
  }
  return s;
})();

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Bonjour 👋 Je suis Léa, l'assistante de votre installateur solaire. Je réponds dans votre langue ! Posez-moi vos questions ou réservez un rendez-vous. / Hi! I reply in your language — ask me anything or book a call.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Désolée, une erreur est survenue. Réessayez dans un instant." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-2xl shadow-accent/40"
      >
        {open ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            className="fixed bottom-28 right-6 z-50 flex h-[540px] w-[92vw] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-2xl"
          >
            <div className="flex items-center gap-3 bg-primary px-5 py-4 text-primary-foreground">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent">
                <Sun className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-display font-bold leading-tight">Léa · Assistante IA</p>
                <p className="flex items-center gap-1 text-xs opacity-80">
                  <Languages className="h-3 w-3" /> Détection automatique de la langue
                </p>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div ref={scrollRef} className="flex flex-col gap-3 p-4">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "self-end bg-accent text-accent-foreground"
                        : "self-start bg-muted text-foreground"
                    }`}
                  >
                    {m.content}
                  </div>
                ))}
                {loading && (
                  <div className="self-start rounded-2xl bg-muted px-4 py-3 text-sm">
                    <span className="inline-flex gap-1">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" />
                    </span>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="flex items-center gap-2 border-t border-border p-3">
              <Input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrivez votre message..."
                className="h-11 rounded-full"
              />
              <Button
                data-testid="chat-send-button"
                onClick={send}
                disabled={loading}
                size="icon"
                className="h-11 w-11 shrink-0 rounded-full bg-accent hover:bg-accent/90"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
